<template>
  <v-dialog v-model="internalValue" max-width="850px" scrollable>
    <v-card class="rounded-xl overflow-hidden">
      <!-- Toolbar Neutro -->
      <v-toolbar color="grey darken-3" dark flat dense>
        <v-icon left small>mdi-clipboard-text-search-outline</v-icon>
        <v-toolbar-title class="text-subtitle-2 font-weight-bold text-uppercase">
          Encuesta de Satisfacción de Capacitación (SIG-CA-RH-FR-005)
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="internalValue = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Tabs: Responder Encuesta / Ver Resultados y Métricas -->
      <v-tabs v-model="activeTab" background-color="grey lighten-4" color="primary" dense>
        <v-tab class="text-caption font-weight-bold text-none">
          <v-icon left small>mdi-pencil-box-outline</v-icon>
          {{ isReadOnly ? 'Ver Respuestas' : 'Formulario de Encuesta' }}
        </v-tab>
        <v-tab class="text-caption font-weight-bold text-none" @click="fetchSummary">
          <v-icon left small>mdi-chart-donut</v-icon>
          Resultados Globales del Evento
        </v-tab>
      </v-tabs>

      <v-card-text class="pa-5" style="max-height: 70vh;">
        <v-tabs-items v-model="activeTab">
          <!-- TAB 1: FORMULARIO DE ENCUESTA -->
          <v-tab-item>
            <!-- Cabecera de Datos Generales -->
            <v-sheet color="grey lighten-4" class="pa-4 rounded-lg mb-4" outlined>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="text-caption grey--text text--darken-2">Tema / Curso:</div>
                  <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                    {{ (eventData && eventData.TevName) || (participantData && participantData.TevName) || 'Capacitación' }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="text-caption grey--text text--darken-2">Participante:</div>
                  <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4">
                    {{ (participantData && participantData.ParticipantFullName) || 'Colaborador' }} (DNI: {{ (participantData && participantData.PrsDocumentNumber) || '-' }})
                  </div>
                </v-col>
                <v-col cols="12" sm="6" class="mt-2">
                  <div class="text-caption grey--text text--darken-2">Capacitador:</div>
                  <div class="text-body-2 font-weight-medium">
                    {{ (eventData && eventData.TrnFullName) || 'Instructor' }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6" class="mt-2">
                  <div class="text-caption grey--text text--darken-2">Fecha:</div>
                  <div class="text-body-2 font-weight-medium">
                    {{ formatDate(eventData && eventData.TevDateInitial ? eventData.TevDateInitial : new Date()) }}
                  </div>
                </v-col>
              </v-row>
            </v-sheet>

            <!-- Instrucciones -->
            <div class="text-caption grey--text text--darken-3 mb-4">
              Por favor marque <b>SÍ</b> o <b>NO</b> con total sinceridad de acuerdo a la experiencia en la capacitación recibida.
            </div>

            <!-- Tabla de Preguntas del Formato Oficial -->
            <v-simple-table dense class="border-rounded elevation-0 mb-4 survey-table">
              <template v-slot:default>
                <thead>
                  <tr class="header-gray">
                    <th class="text-left font-weight-bold" style="width: 75%;">PREGUNTA / CRITERIO DE EVALUACIÓN</th>
                    <th class="text-center font-weight-bold" style="width: 25%;">RESPUESTA</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- I. INICIO -->
                  <tr class="sub-header-row">
                    <td colspan="2" class="font-weight-bold text-caption text-uppercase primary--text">
                      I. Inicio
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span class="font-weight-bold">1.</span> ¿El expositor dio a conocer los objetivos y los temas que se desarrollarán en el curso?
                    </td>
                    <td class="text-center">
                      <v-radio-group v-model="form.SurQ1Objectives" row dense hide-details class="justify-center mt-0" :disabled="isReadOnly">
                        <v-radio label="SÍ" :value="true" color="success"></v-radio>
                        <v-radio label="NO" :value="false" color="error"></v-radio>
                      </v-radio-group>
                    </td>
                  </tr>

                  <!-- II. DESARROLLO -->
                  <tr class="sub-header-row">
                    <td colspan="2" class="font-weight-bold text-caption text-uppercase primary--text">
                      II. Desarrollo
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span class="font-weight-bold">2.</span> ¿El expositor facilitó la aplicación de la capacitación mediante el desarrollo de ejercicios prácticos o casos reales?
                    </td>
                    <td class="text-center">
                      <v-radio-group v-model="form.SurQ2Examples" row dense hide-details class="justify-center mt-0" :disabled="isReadOnly">
                        <v-radio label="SÍ" :value="true" color="success"></v-radio>
                        <v-radio label="NO" :value="false" color="error"></v-radio>
                      </v-radio-group>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span class="font-weight-bold">3.</span> ¿Se cumplieron los objetivos del curso?
                    </td>
                    <td class="text-center">
                      <v-radio-group v-model="form.SurQ3MetTopics" row dense hide-details class="justify-center mt-0" :disabled="isReadOnly">
                        <v-radio label="SÍ" :value="true" color="success"></v-radio>
                        <v-radio label="NO" :value="false" color="error"></v-radio>
                      </v-radio-group>
                    </td>
                  </tr>

                  <!-- III. PROVECHO -->
                  <tr class="sub-header-row">
                    <td colspan="2" class="font-weight-bold text-caption text-uppercase primary--text">
                      III. Provecho
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span class="font-weight-bold">4.</span> ¿Considera que el curso impartido le ha permitido desarrollar o fortalecer competencias y conocimientos que faciliten el desarrollo de sus labores?
                    </td>
                    <td class="text-center">
                      <v-radio-group v-model="form.SurQ4Satisfaction" row dense hide-details class="justify-center mt-0" :disabled="isReadOnly">
                        <v-radio label="SÍ" :value="true" color="success"></v-radio>
                        <v-radio label="NO" :value="false" color="error"></v-radio>
                      </v-radio-group>
                    </td>
                  </tr>

                  <!-- IV. TIEMPO -->
                  <tr class="sub-header-row">
                    <td colspan="2" class="font-weight-bold text-caption text-uppercase primary--text">
                      IV. Tiempo
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span class="font-weight-bold">5.</span> ¿El tiempo asignado a los temas desarrollados fue suficiente?
                    </td>
                    <td class="text-center">
                      <v-radio-group v-model="form.SurQ5TimeSufficient" row dense hide-details class="justify-center mt-0" :disabled="isReadOnly">
                        <v-radio label="SÍ" :value="true" color="success"></v-radio>
                        <v-radio label="NO" :value="false" color="error"></v-radio>
                      </v-radio-group>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Comentarios / Sugerencias -->
            <div class="mt-3">
              <div class="text-caption font-weight-bold grey--text text--darken-3 mb-1">
                COMENTARIOS O SUGERENCIAS PARA EL CAPACITADOR / MEJORA CONTINUA:
              </div>
              <v-textarea
                v-model="form.SurComments"
                outlined
                dense
                rows="3"
                placeholder="Escriba comentarios, sugerencias o aspectos a mejorar..."
                :disabled="isReadOnly"
                class="rounded-lg"
              ></v-textarea>
            </div>
          </v-tab-item>

          <!-- TAB 2: RESUMEN DE RESULTADOS Y SATISFACCIÓN -->
          <v-tab-item>
            <div v-if="loadingSummary" class="text-center py-5">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="text-caption mt-2">Cargando métricas de satisfacción...</div>
            </div>

            <div v-else-if="summaryData">
              <!-- KPI Cards -->
              <v-row dense class="mb-4">
                <v-col cols="12" sm="6">
                  <v-card class="pa-4 rounded-xl border-light" outlined elevation="0">
                    <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                      Total Respuestas Registradas
                    </div>
                    <div class="text-h4 font-weight-black grey--text text--darken-4 mt-1">
                      {{ summaryData.TotalSurveys || 0 }}
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-card class="pa-4 rounded-xl border-light" outlined elevation="0">
                    <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                      Índice de Satisfacción Global
                    </div>
                    <div class="text-h4 font-weight-black mt-1" :class="getScoreColorClass(summaryData.OverallSatisfactionPct)">
                      {{ summaryData.OverallSatisfactionPct }}%
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Barras de Aprobación por Pregunta -->
              <v-card class="pa-4 rounded-xl border-light mb-4" outlined elevation="0">
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 mb-3 text-uppercase">
                  Cumplimiento por Pregunta
                </div>

                <div v-for="(q, idx) in questionMetrics" :key="'qm-' + idx" class="mb-3">
                  <div class="d-flex justify-space-between text-caption font-weight-medium mb-1">
                    <span>{{ q.label }}</span>
                    <b>{{ q.pct }}% Satisfacción</b>
                  </div>
                  <v-progress-linear
                    :value="q.pct"
                    height="10"
                    rounded
                    :color="q.pct >= 80 ? 'success' : (q.pct >= 60 ? 'warning' : 'error')"
                  ></v-progress-linear>
                </div>
              </v-card>

              <!-- Comentarios Recibidos -->
              <v-card class="pa-4 rounded-xl border-light" outlined elevation="0" v-if="surveyList.length > 0">
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 mb-3 text-uppercase">
                  Comentarios y Sugerencias de Participantes
                </div>
                <v-list dense>
                  <template v-for="(item, iIdx) in surveyListWithComments">
                    <v-list-item :key="'com-' + iIdx">
                      <v-list-item-avatar color="grey lighten-4" size="30">
                        <v-icon small color="grey darken-3">mdi-comment-text-outline</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold text-caption">
                          {{ item.ParticipantFullName }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="grey--text text--darken-3 text-wrap">
                          "{{ item.SurComments }}"
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider :key="'div-' + iIdx" v-if="iIdx < surveyListWithComments.length - 1"></v-divider>
                  </template>
                </v-list>
              </v-card>
            </div>

            <v-alert v-else type="info" outlined dense class="rounded-lg">
              No hay respuestas registradas para este evento de capacitación aún.
            </v-alert>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="py-3 px-5 bg-white d-flex justify-end">
        <v-btn text color="grey darken-2" @click="internalValue = false">
          {{ isReadOnly || activeTab === 1 ? 'Cerrar' : 'Cancelar' }}
        </v-btn>
        <v-btn
          v-if="!isReadOnly && activeTab === 0"
          color="success"
          class="font-weight-bold px-4 text-none rounded-lg"
          elevation="0"
          :loading="saving"
          @click="saveSurvey"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Guardar Encuesta
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TrainingParticipantService from "@/services/Management/TrainingParticipantService";

export default {
  name: "TrainingSurveyModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    participant: {
      type: Object,
      default: () => ({}),
    },
    event: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      activeTab: 0,
      saving: false,
      loadingSummary: false,
      summaryData: null,
      surveyList: [],
      form: {
        SurID: 0,
        TevID: 0,
        TseID: null,
        TpaID: 0,
        NtpID: null,
        SurQ1Objectives: true,
        SurQ2Examples: true,
        SurQ3MetTopics: true,
        SurQ4Satisfaction: true,
        SurQ5TimeSufficient: true,
        SurComments: "",
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    participantData() {
      return this.participant || {};
    },
    eventData() {
      return this.event || {};
    },
    surveyListWithComments() {
      return this.surveyList.filter((s) => s.SurComments && s.SurComments.trim().length > 0);
    },
    questionMetrics() {
      if (!this.summaryData || !this.summaryData.TotalSurveys) return [];
      return [
        {
          label: "1. ¿Expositor dio a conocer objetivos y temas?",
          pct: Math.round(this.summaryData.PctQ1Objectives || 0),
        },
        {
          label: "2. ¿Facilitó aplicación con casos prácticos?",
          pct: Math.round(this.summaryData.PctQ2Examples || 0),
        },
        {
          label: "3. ¿Se cumplieron los objetivos del curso?",
          pct: Math.round(this.summaryData.PctQ3MetTopics || 0),
        },
        {
          label: "4. ¿Fortaleció competencias para sus labores?",
          pct: Math.round(this.summaryData.PctQ4Satisfaction || 0),
        },
        {
          label: "5. ¿Tiempo asignado fue suficiente?",
          pct: Math.round(this.summaryData.PctQ5TimeSufficient || 0),
        },
      ];
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.activeTab = this.initialTab;
        this.initFormData();
        if (this.activeTab === 1) {
          this.fetchSummary();
        }
      }
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const parts = String(dateStr).split("T")[0].split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return dateStr;
    },
    initFormData() {
      const tevId = this.participantData.TevID || this.eventData.TevID || 0;
      const tpaId = this.participantData.TpaID || 0;
      this.form = {
        SurID: 0,
        TevID: tevId,
        TseID: null,
        TpaID: tpaId,
        NtpID: this.participantData.NtpID || null,
        SurQ1Objectives: true,
        SurQ2Examples: true,
        SurQ3MetTopics: true,
        SurQ4Satisfaction: true,
        SurQ5TimeSufficient: true,
        SurComments: "",
      };

      if (tpaId > 0 && tevId > 0) {
        const reqId = this.$fun.getUserID();
        TrainingParticipantService.listSurveys({ TevID: tevId }, reqId)
          .then((res) => {
            if (res.data && res.data.length > 0) {
              const existing = res.data.find((s) => s.TpaID === tpaId);
              if (existing) {
                this.form = {
                  SurID: existing.SurID || 0,
                  TevID: existing.TevID,
                  TseID: existing.TseID,
                  TpaID: existing.TpaID,
                  NtpID: existing.NtpID,
                  SurQ1Objectives: existing.SurQ1Objectives === 1 || existing.SurQ1Objectives === true,
                  SurQ2Examples: existing.SurQ2Examples === 1 || existing.SurQ2Examples === true,
                  SurQ3MetTopics: existing.SurQ3MetTopics === 1 || existing.SurQ3MetTopics === true,
                  SurQ4Satisfaction: existing.SurQ4Satisfaction === 1 || existing.SurQ4Satisfaction === true,
                  SurQ5TimeSufficient: existing.SurQ5TimeSufficient === 1 || existing.SurQ5TimeSufficient === true,
                  SurComments: existing.SurComments || "",
                };
              }
            }
          })
          .catch(() => {});
      }
    },
    saveSurvey() {
      this.saving = true;
      const reqId = this.$fun.getUserID();
      const payload = {
        SurID: this.form.SurID || 0,
        TevID: this.participantData.TevID || this.eventData.TevID || this.form.TevID,
        TseID: this.form.TseID || null,
        TpaID: this.participantData.TpaID || this.form.TpaID || null,
        NtpID: this.participantData.NtpID || this.form.NtpID || null,
        SurQ1Objectives: this.form.SurQ1Objectives ? 1 : 0,
        SurQ2Examples: this.form.SurQ2Examples ? 1 : 0,
        SurQ3MetTopics: this.form.SurQ3MetTopics ? 1 : 0,
        SurQ4Satisfaction: this.form.SurQ4Satisfaction ? 1 : 0,
        SurQ5TimeSufficient: this.form.SurQ5TimeSufficient ? 1 : 0,
        SurComments: this.form.SurComments || "",
      };

      TrainingParticipantService.saveSurvey(payload, reqId)
        .then(() => {
          this.$fun.alert("Encuesta de satisfacción registrada correctamente.", "success");
          this.$emit("saved");
          this.internalValue = false;
        })
        .catch((err) => {
          this.$fun.alert(err?.response?.data?.Message || "Error al guardar la encuesta de satisfacción.", "error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    fetchSummary() {
      const tevId = this.participantData.TevID || this.eventData.TevID || 0;
      if (!tevId) return;

      this.loadingSummary = true;
      const reqId = this.$fun.getUserID();
      Promise.all([
        TrainingParticipantService.summarySurvey({ TevID: tevId }, reqId),
        TrainingParticipantService.listSurveys({ TevID: tevId }, reqId),
      ])
        .then(([sumRes, listRes]) => {
          this.summaryData = sumRes.data || null;
          this.surveyList = listRes.data || [];
        })
        .catch(() => {
          this.summaryData = null;
        })
        .finally(() => {
          this.loadingSummary = false;
        });
    },
    getScoreColorClass(pct) {
      if (pct >= 80) return "green--text text--darken-3";
      if (pct >= 60) return "amber--text text--darken-3";
      return "red--text";
    },
  },
};
</script>

<style scoped>
.survey-table th,
.survey-table td {
  padding: 8px 12px !important;
  font-size: 13px !important;
}

.header-gray {
  background-color: #f1f5f9;
}

.sub-header-row {
  background-color: #f8fafc;
}
</style>
